<template>
  <div
    ref="parallaxContainerRef"
    v-editable="blok"
    class="CoverBlock"
    :class="{
      'heroIfFirst': blok.heroIfFirst,
    }"
  >
    <div
      :style="`background-color: ${blok.backgroundColor.color}`"
      class="relative aspect-portrait desk:aspect-[2/1] max-h-[calc(100dvh-160px)] min-w-full overflow-hidden"
    >
      <div
        v-if="blok.imageOrBackgroundVideo && blok.imageOrBackgroundVideo.filename"
        :style="blok.parallaxEffect ? { transform: `translateY(${offset}px) scale(1.2)`, transformOrigin: 'bottom center' } : {}"
        class="w-full h-full absolute top-0 left-0 transition-all duration-[50ms]"
      >
        <video
          v-if="checkIfVideoFile(blok.imageOrBackgroundVideo.filename)"
          ref="mobileVideoRef"
          width="w-full"
          height="h-full"
          class="w-full h-full object-cover"
          loop
          muted
          autoplay
          playsinline
          preload="auto"
        >
          <source :src="blok.imageOrBackgroundVideo.filename" type="video/mp4">
        </video>
        <nuxt-img
          v-else-if="blok.imageOrBackgroundVideo && blok.imageOrBackgroundVideo.filename"
          preset="standard"
          sizes="sm:100vw desk:1024px deskStd:1372px deskLarge:1600px"
          class="h-full w-full object-cover"
          :class="blok.focalPoint"
          :preload="isTop"
          :loading="isTop ? 'eager':'lazy'"
          :fetchpriority="isTop ? 'high' : undefined"
          :src="blok.imageOrBackgroundVideo.filename"
          :alt="blok.imageOrBackgroundVideo.alt"
        />
      </div>
      <div
        v-if="blok.desktopImageOrBackgroundVideo && blok.desktopImageOrBackgroundVideo.filename"
        class="hidden desk:block w-full h-full absolute top-0 left-0"
      >
        <video
          v-if="checkIfVideoFile(blok.desktopImageOrBackgroundVideo.filename)"
          ref="desktopVideoRef"
          width="w-full"
          height="h-full"
          class="w-full h-full object-cover"
          loop
          muted
          autoplay
          playsinline
          preload="auto"
        >
          <source :src="blok.desktopImageOrBackgroundVideo.filename" type="video/mp4">
        </video>
        <nuxt-img
          v-else-if="blok.desktopImageOrBackgroundVideo && blok.desktopImageOrBackgroundVideo.filename"
          preset="standard"
          sizes="sm:100vw desk:1260px"
          class="h-full w-full object-cover"
          :class="blok.desktopFocalPoint"
          :preload="isTop"
          :loading="isTop ? 'eager':'lazy'"
          :fetchpriority="isTop ? 'high' : undefined"
          :src="blok.desktopImageOrBackgroundVideo.filename"
          :alt="blok.desktopImageOrBackgroundVideo.alt"
        />
      </div>
      <div
        :style="`opacity: ${blok.imageOverlay?.value}`"
        class="absolute top-0 left-0 w-full h-full bg-darkest"
      />
      <div
        class="layout-container h-full relative flex"
        :class="{
          'items-start': blok.contentAlign === 'top',
          'items-center': blok.contentAlign === 'center',
          'items-end': blok.contentAlign === 'bottom',
        }"
      >
        <div
          class="flex flex-col gap-12 w-full py-32 desk:py-64"
          :class="{
            'text-center items-center': blok.textAlign === 'center',
            'text-left items-start': blok.textAlign !== 'center',
            'text-text-inverse': blok.taglineAndHeadlineInvertedText,
          }"
        >
          <div v-if="blok.tagline" class="type-tagline-sm">
            {{ blok.tagline }}
          </div>
          <component
            :is="blok.headerTag ?? 'h1'"
            v-if="blok.headline"
            class="type-headline-4xl max-w-[16ch]"
          >
            {{ blok.headline }}
          </component>
          <div
            v-if="blok.content"
            class="wysiwyg max-w-[56ch]"
            :class="{ 'text-inverse': blok.taglineAndHeadlineInvertedText }"
          >
            <ExtendedWysiwyg :content="blok.content" />
          </div>
          <div v-if="blok.buttonText" class="flex flex-wrap gap-20 desk:gap-24 mt-12">
            <nuxt-link
              v-if="blok.buttonText"
              :to="$sbLinkFix(blok.link)"
              :target="$sbLinkTarget(blok.link)"
            >
              <div
                class="btn"
                :class="{
                  'btn--secondary' : blok.buttonStyle === 'secondary',
                  'btn--underlineInv' : blok.buttonStyle === 'underline',
                  'btn--ghost' : blok.buttonStyle === 'ghost',
                }"
              >
                {{ blok.buttonText }}
              </div>
            </nuxt-link>
            <nuxt-link
              v-if="blok.secondaryButtonText"
              :to="$sbLinkFix(blok.secondaryLink)"
              :target="$sbLinkTarget(blok.secondaryLink)"
            >
              <div
                class="btn"
                :class="{
                  'btn--secondary' : blok.secondaryButtonStyle === 'secondary',
                  'btn--underlineInv' : blok.secondaryButtonStyle === 'underline',
                  'btn--ghost' : blok.secondaryButtonStyle === 'ghost',
                }"
              >
                {{ blok.secondaryButtonText }}
              </div>
            </nuxt-link>
          </div>
        </div>
      </div>
      <div
        class="absolute top-12 left-12 desk:top-32 desk:left-32 w-64 h-64 desk:w-112 desk:h-112"
        :class="{ 'spin': blok.spinSplash, }"
      >
        <div
          v-if="blok.splashTopText || blok.splashText"
          class="h-full w-full rounded-full flex flex-col items-center justify-center gap-2 text-text-inverse text-center"
          :class="{
            'bg-bg-inverse': blok.splashBackgroundColor === 'default',
            'bg-bg-errorStrong': blok.splashBackgroundColor === 'sale',
          }"
        >
          <div
            v-if="blok.splashTopText"
            class="-mt-4 mobOnly:mb-0 mb-2 mobOnly:type-tagline type-tagline-sm leading-single"
          >
            {{ blok.splashTopText }}
          </div>
          <div
            v-if="blok.splashText"
            class="type-headline-md desk:type-headline-xl leading-single"
          >
            {{ blok.splashText }}
          </div>
        </div>
        <div v-else class="w-full h-full rounded-full overflow-hidden">
          <nuxt-img
            v-if="blok.splashImage && blok.splashImage.filename"
            preset="standard"
            loading="lazy"
            :sizes="`sm:100vw desk:200px`"
            :src="blok.splashImage.filename"
            :alt="blok.splashImage.alt"
          />
        </div>
      </div>
      <div
        class="absolute bottom-12 right-12 desk:bottom-32 desk:right-32 w-64 h-64 desk:w-112 desk:h-112"
        :class="{ 'spin': blok.spinDiscountSplash, }"
      >
        <div
          v-if="blok.splashDiscountText || blok.splashDiscountHeading"
          class="h-full w-full rounded-full flex flex-col items-center justify-center gap-2 text-text-inverse text-center"
          :class="{
            'bg-bg-inverse': blok.splashDiscountBackgroundColor === 'default',
            'bg-bg-errorStrong': blok.splashDiscountBackgroundColor === 'sale',
          }"
        >
          <div
            v-if="blok.splashDiscountText"
            class="-mt-4 mobOnly:mb-0 mb-2 mobOnly:type-tagline type-tagline-sm leading-single"
          >
            {{ blok.splashDiscountText }}
          </div>
          <div
            v-if="blok.splashDiscountHeading"
            class="type-headline-md desk:type-headline-xl leading-single"
          >
            {{ blok.splashDiscountHeading }}
          </div>
        </div>
        <div v-else class="w-full h-full rounded-full overflow-hidden">
          <nuxt-img
            v-if="blok.splashDiscountImage && blok.splashDiscountImage.filename"
            preset="standard"
            loading="lazy"
            :sizes="`sm:100vw desk:200px`"
            :src="blok.splashDiscountImage.filename"
            :alt="blok.splashDiscountImage.alt"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  StoryBlokAsset,
  StoryBlokLink,
  StoryBlokColorpicker,
  StoryBlokVisualApperance, StoryBlokSlider, DatasourceObjectPosition, DatasourceHeadlineTypes
} from '#constants/types/storyblok';
import type { ISbRichtext } from 'storyblok-js-client';
import { checkIfVideoFile, convertObjectPositionToBgPosition, storyblokScaleImage } from '~/util/imageUtil';
import { useElementBounding } from '@vueuse/core';
import { computed, ref } from 'vue';

const NuxtLink = resolveComponent('NuxtLink');

interface SplashSelector {
  _uid: string;
  splash: string;
  splashImage: StoryBlokAsset;
}

interface Props {
  _uid: string;
  tagline: string;
  headline: string;
  headerTag?: DatasourceHeadlineTypes;
  taglineAndHeadlineInvertedText: boolean;
  textAlign: 'left' | 'center';
  contentAlign: 'top' | 'center' | 'bottom';
  content: ISbRichtext;
  buttonText: string;
  buttonStyle: 'primary' | 'secondary' | 'ghost' | 'underline';
  link: StoryBlokLink;
  secondaryButtonText: string;
  secondaryButtonStyle: 'primary' | 'secondary' | 'ghost' | 'underline';
  secondaryLink: StoryBlokLink;
  imageOrBackgroundVideo: StoryBlokAsset;
  desktopImageOrBackgroundVideo: StoryBlokAsset;
  image: StoryBlokAsset;
  desktopImage: StoryBlokAsset;
  backgroundColor: StoryBlokColorpicker;
  design: 'default' | 'oneColumn';
  topLeftSplash: SplashSelector[];
  bottomRightSplash: SplashSelector[];
  visualAppearance: StoryBlokVisualApperance;
  splashTopText: string,
  splashText: string,
  splashBackgroundColor: 'default' | 'sale';
  spinSplash: boolean,
  splashImage: StoryBlokAsset,
  splashDiscountImage: StoryBlokAsset,
  splashDiscountNumber: number,
  splashDiscountText: string,
  splashDiscountHeading: string,
  splashDiscountBackgroundColor: 'default' | 'sale';
  spinDiscountSplash: boolean;
  tapeBackgroundColor: StoryBlokColorpicker;
  tapeText: string;
  heroIfFirst: boolean;
  imageOverlay: StoryBlokSlider;
  focalPoint: DatasourceObjectPosition;
  desktopFocalPoint: DatasourceObjectPosition;
  parallaxEffect: boolean;
}

const props = defineProps<{
  blok: Props,
  placement?: number,
}>();

const showTapes = computed(()=> {
  if (props.blok.splashImage &&  props.blok.splashImage.filename && props.blok.splashImage.filename !== '') {
    return false;
  }
  if (props.blok.splashDiscountImage &&  props.blok.splashDiscountImage.filename && props.blok.splashDiscountImage.filename !== '') {
    return false;
  }
  return props.blok.tapeText && props.blok.tapeBackgroundColor.color
      && props.blok.image && props.blok.image.filename
      && !props.blok.splashDiscountNumber && !props.blok.splashDiscountText;

});

const spashImageSizes = computed(()=> {
  return props.blok.design === 'oneColumn' ?
    'sm:100vw desk:896px deskStd:1244px deskLarge:1472px':
    'sm:100vw desk:436px deskStd:610px deskLarge:724px';
});

const isTop = computed(()=> {
  return (props.placement === 0);
});

const parallaxContainerRef = useTemplateRef('parallaxContainerRef');
const { top } = useElementBounding(parallaxContainerRef);

const mobileVideoRef = ref<HTMLVideoElement | null>(null);
const desktopVideoRef = ref<HTMLVideoElement | null>(null);
const route = useRoute();

const windowHeight = ref(0);

const playVideos = () => {
  const videos = [mobileVideoRef.value, desktopVideoRef.value].filter(Boolean) as HTMLVideoElement[];
  videos.forEach((video) => {
    if (video.paused) {
      if (video.readyState < 2) {
        video.load();
      }
      video.play().catch((err) => {
        console.warn('Failed to play video:', err);
      });
    }
  });
};

onMounted(()=> {
  windowHeight.value = window.innerHeight;

  // Attempt to play videos on mount
  nextTick(() => {
    playVideos();
  });

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      playVideos();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  });
});

onActivated(() => {
  nextTick(() => {
    playVideos();
  });
});

watch(() => route.path, () => {
  setTimeout(() => {
    playVideos();
  }, 100);
});

const progress = computed(() => {
  const start = windowHeight.value;
  const end = -600;
  return Math.max(0, Math.min((start - top.value) / (start - end), 1));
});

const offset = computed(() => progress.value * 140);

</script>

<style scoped lang="postcss">
:deep(.wysiwyg h1) {
  @apply text-36 desk:text-56;
}

:deep(.wysiwyg p) {
  @apply text-18 desk:text-20 leading-26 desk:leading-28 mt-16;
}

/* Only apply inverse text styling when the parent has the "text-inverse" class */
:deep(.wysiwyg.text-inverse p),
:deep(.wysiwyg.text-inverse h1),
:deep(.wysiwyg.text-inverse h2),
:deep(.wysiwyg.text-inverse h3),
:deep(.wysiwyg.text-inverse h4),
:deep(.wysiwyg.text-inverse h5),
:deep(.wysiwyg.text-inverse h6) {
  @apply !text-text-inverse;
}

.spin {
  animation-name: spin;
  animation-duration: 20000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}
</style>
